import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

export type SaveState = 'idle' | 'saving' | 'saved' | 'error';

export interface UseReportAutoSaveResult {
  saveState: SaveState;
  lastSavedAt: Date | null;
  hasUnsaved: boolean;
  /** Debounced save. Subsequent calls with the same key replace the pending fn and reset the timer. */
  scheduleSave: (key: string, save: () => Promise<unknown>) => void;
  /** Immediate save (for discrete actions like add/delete/reorder). */
  runSave: <T>(op: () => Promise<T>) => Promise<T | undefined>;
  /** Re-run the last failed save. No-op when nothing has failed. */
  retry: () => void;
  /** Fire all pending scheduled saves immediately. */
  flush: () => Promise<void>;
}

export interface UseReportAutoSaveOptions {
  debounceMs?: number;
}

export function useReportAutoSave(
  options?: UseReportAutoSaveOptions,
): UseReportAutoSaveResult {
  const debounceMs = options?.debounceMs ?? 1500;
  const [saveState, setSaveState] = useState<SaveState>('idle');
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const pendingFns = useRef<Map<string, () => Promise<unknown>>>(new Map());
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const inFlight = useRef<Set<string>>(new Set());
  const failedFn = useRef<(() => Promise<unknown>) | null>(null);

  const hasOutstanding = useCallback(
    () =>
      pendingFns.current.size > 0 ||
      inFlight.current.size > 0 ||
      failedFn.current !== null,
    [],
  );

  const runOne = useCallback(async (key: string, fn: () => Promise<unknown>) => {
    inFlight.current.add(key);
    setSaveState('saving');
    try {
      await fn();
      inFlight.current.delete(key);
      // Clear failed buffer only when *this* save was the failed one being retried,
      // or when nothing else is outstanding (a successful subsequent edit also
      // resolves the prior failure).
      if (failedFn.current === fn || !hasOutstanding()) {
        failedFn.current = null;
      }
      setLastSavedAt(new Date());
      if (pendingFns.current.size === 0 && inFlight.current.size === 0) {
        setSaveState(failedFn.current ? 'error' : 'saved');
      }
    } catch (err) {
      console.error('Auto-save failed:', err);
      inFlight.current.delete(key);
      failedFn.current = fn;
      setSaveState('error');
    }
  }, [hasOutstanding]);

  const scheduleSave = useCallback(
    (key: string, save: () => Promise<unknown>) => {
      pendingFns.current.set(key, save);
      const existing = timers.current.get(key);
      if (existing) clearTimeout(existing);
      setSaveState('saving');

      const t = setTimeout(() => {
        const fn = pendingFns.current.get(key);
        pendingFns.current.delete(key);
        timers.current.delete(key);
        if (fn) void runOne(key, fn);
      }, debounceMs);
      timers.current.set(key, t);
    },
    [debounceMs, runOne],
  );

  const runSave = useCallback(
    async <T,>(op: () => Promise<T>): Promise<T | undefined> => {
      setSaveState('saving');
      try {
        const result = await op();
        setLastSavedAt(new Date());
        if (pendingFns.current.size === 0 && inFlight.current.size === 0) {
          setSaveState(failedFn.current ? 'error' : 'saved');
        }
        return result;
      } catch (err) {
        console.error('Save failed:', err);
        setSaveState('error');
        return undefined;
      }
    },
    [],
  );

  const retry = useCallback(() => {
    const fn = failedFn.current;
    if (!fn) return;
    failedFn.current = null;
    void runOne('__retry__', fn);
  }, [runOne]);

  const flush = useCallback(async () => {
    const entries = Array.from(pendingFns.current.entries());
    pendingFns.current.clear();
    timers.current.forEach((t) => clearTimeout(t));
    timers.current.clear();
    await Promise.all(entries.map(([key, fn]) => runOne(key, fn)));
  }, [runOne]);

  // Flash "Saved" briefly after a clean save, then return to idle.
  useEffect(() => {
    if (saveState !== 'saved') return;
    const t = setTimeout(() => setSaveState('idle'), 1600);
    return () => clearTimeout(t);
  }, [saveState]);

  // Warn before unload when there's an in-flight or failed save. The browser
  // shows its generic confirmation when returnValue is set.
  const hasUnsaved = saveState === 'saving' || saveState === 'error';
  useEffect(() => {
    if (!hasUnsaved) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsaved]);

  // Clear timers on unmount so a slow PATCH doesn't fire after the component
  // is gone. Pending work is dropped — callers can flush() before navigating.
  useEffect(() => {
    const localTimers = timers.current;
    return () => {
      localTimers.forEach((t) => clearTimeout(t));
      localTimers.clear();
    };
  }, []);

  // Auto-retry when the network comes back. The browser fires `online` on
  // navigator when connectivity is restored — if there's a buffered failure
  // at that moment, transparently re-PATCH it instead of waiting for the
  // user to click the Retry chip. Idempotent: retry() is a no-op when
  // failedFn is null.
  useEffect(() => {
    const handler = () => retry();
    window.addEventListener('online', handler);
    return () => window.removeEventListener('online', handler);
  }, [retry]);

  return { saveState, lastSavedAt, hasUnsaved, scheduleSave, runSave, retry, flush };
}

// Context lets nested editor children (SectionCard, ItemCard) schedule
// debounced saves without prop-drilling through every level.
const AutoSaveContext = createContext<UseReportAutoSaveResult | null>(null);

export const AutoSaveProvider = AutoSaveContext.Provider;

export function useAutoSaveContext(): UseReportAutoSaveResult | null {
  return useContext(AutoSaveContext);
}
